(ns frontend.view.nev.kyc.process.board-member-editor
  (:require
   [clojure.data :as data]
   [common.localization :as loc]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.schema.entities.workspace.kyc.templates.template-registry :as template-registry]
   [frontend.db :as db]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.dynamic.view :as dynamic-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.nev.kyc.process.ubo-editor :as ubo-editor]
   [frontend.view.nev.kyc.process-status-views.view-utils :as view-utils]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.util :as uu]))

(defn open-board-member-editor
  [{:keys [idx existing-board-member kyc-entity-editor process-id state-atom
           lang-kw question-attribute question-meta answers-atom mandatory? read-only?
           save!]}]
  (let [kyc-process (session/get-entity process-id)
        kyc-process-status (:kyc-process/status kyc-process)
        wip-screen? (= kyc-process-status :kyc-process.status/wip)
        board-member-answers
        (r/atom (or existing-board-member
                    {:kyc-board-member/confirmation-status
                     (if wip-screen? :confirmed-by-customer :confirmed-by-controller)}))
        field-errors-atom (r/atom {})
        board-members (get @answers-atom question-attribute)
        board-member-template-schema (:data/template question-meta)
        calc-new-board-member-answer (fn []
                                       (if (>= idx (count board-members))
                                         (concat board-members
                                                 [@board-member-answers])
                                         (->> board-members
                                              (map-indexed (fn [uidx board-member]
                                                             (if (= uidx idx)
                                                               @board-member-answers
                                                               board-member)))
                                              (vec))))]
    (modals/open-modal!
     dynamic-modal/modal-key
     {:title            (if existing-board-member
                          (loc/tr :board-member-edit nil lang-kw)
                          (loc/tr :board-member-add nil lang-kw))
      :render-fn
      (fn [_state]
        [:div
         [kyc-entity-editor
          {:container-id    (str "BOARD-MEMBER-" idx)
           :process-id      process-id
           :state-atom      state-atom
           :save!           identity
           :mandatory?      mandatory?
           :skip-info?      true
           :read-only?      read-only?
           :answers-atom    board-member-answers
           :field-errors-atom field-errors-atom
           :template-schema board-member-template-schema
           :lang-kw         lang-kw}]
         (when (and (not read-only?) existing-board-member)
           [:div.container
            [button/warning-solid
             {:on-click (fn []
                          (modals/open-modal!
                           confirmation-modal/modal-key
                           {:body           (loc/tr :delete-board-member-confirmation nil lang-kw)
                            :confirm-text   (loc/tr :delete-board-member nil lang-kw)
                            :confirm-icon   ""
                            :skip-checkbox? true
                            :on-confirm     (fn [_]
                                              (swap! answers-atom assoc
                                                     question-attribute
                                                     (concat (take idx board-members)
                                                             (drop (inc idx) board-members)))
                                              (modals/close-modal! nil (ffirst @db/modals))
                                              (save!))}))}
             (loc/tr :delete-permanently nil lang-kw)]])])
      :show-button?     (not read-only?)
      :button-text      (loc/tr :save nil lang-kw)
      :custom-validator-fn
      (fn [_state]
        (let [[old-only new-only _] (data/diff existing-board-member @board-member-answers)
              changes? (or (seq old-only)
                           (seq new-only))]
          (view-utils/modal-validator-tooltip
           {:lang-kw           lang-kw
            :template-schema   board-member-template-schema
            :answers-atom      board-member-answers
            :field-errors-atom field-errors-atom
            :kyc-process       kyc-process
            :changes?          changes?})))
      :on-submit!       (fn [_]
                          (let [new-value (calc-new-board-member-answer)]
                            (swap! answers-atom assoc question-attribute new-value)
                            (save!)))
      :on-before-close! (fn [close!]
                          (if (= (get @answers-atom question-attribute)
                                 (calc-new-board-member-answer))
                            (close!)
                            (modals/open-modal!
                             confirmation-modal/modal-key
                             {:body           (loc/tr :confirm-close-kyc-modal-without-saving nil lang-kw)
                              :confirm-text   (loc/tr :close-without-saving nil lang-kw)
                              :confirm-icon   ""
                              :skip-checkbox? true
                              :on-confirm     #(close!)})))})))

(defn is-ubo?                                                                                       ; Todo what should be the exact match criteria?
  "Check if board member is already set as UBO. True if first name and last name are same."
  [board-member answers-atom]
  (let [ubos (:kyc-legal-entity/ubo-edn @answers-atom)
        board-member-first-name (:kyc-board-member/first-name board-member)
        board-member-last-name (:kyc-board-member/last-name board-member)]
    (->> ubos
         (some (fn [ubo]
                 (let [ubo-first-name (:kyc-ubo/first-name ubo)
                       ubo-last-name (:kyc-ubo/last-name ubo)]
                   (and
                    (= ubo-first-name board-member-first-name)
                    (= ubo-last-name board-member-last-name))))))))

(defn translate-board-role
  [role lang-kw]
  (let [translations {:chair-of-the-board      {:fi "Puheenjohtaja" :en "Chair of the board"}
                      :vice-chair-of-the-board {:fi "Hallituksen varapuheenjohtaja" :en "Vice chair of the board"}
                      :board-member            {:fi "Hallituksen jäsen" :en "Board member"}
                      :deputy-board-member     {:fi "Hallituksen varajäsen" :en "Deputy board member"}
                      :unknown-board-role      {:fi "" :en ""}}                                     ; Better to not show anything? -> indicate to user that they need to manually fill it
        role (get-in translations [role lang-kw])]
    role))

(defn get-confirmation-status-label
  [confirmation-status kyc-process-status lang-kw]
  (let [translations
        {:pending                    {:kyc-process.status/draft {:fi "Odottaa vahvistusta" :en "Pending"}
                                      :kyc-process.status/wip   {:fi "Odottaa vahvistusta" :en "Pending"}}
         :confirmed-by-controller    {:kyc-process.status/draft {:fi "Vahvistettu (hallinnoija)"
                                                                 :en "Confirmed by controller"}
                                      :kyc-process.status/wip   {:fi "Odottaa" :en "Pending"}}

         :confirmed-by-customer      {:kyc-process.status/draft {:fi "Vahvistettu (asiakas)"
                                                                 :en "Confirmed by customer"}
                                      :kyc-process.status/wip   {:fi "Vahvistettu" :en "Confirmed"}}

         :removed-by-controller      {:kyc-process.status/draft {:fi "Poistettu (hallinnoija)"
                                                                 :en "Removed by controller"}
                                      :kyc-process.status/wip   {:fi "Poistettu" :en "Removed"}}

         :removed-by-customer        {:kyc-process.status/draft {:fi "Poistettu (asiakas)"
                                                                 :en "Removed by customer"}
                                      :kyc-process.status/wip   {:fi "Poistettu" :en "Removed"}}

         :not-found-from-data-source {:kyc-process.status/draft {:fi "Ei löytynut datalähteestä"
                                                                 :en "Not found from data source"}
                                      :kyc-process.status/wip   {:fi "Odottaa" :en "Pending"}}

         :not-found                  {:kyc-process.status/draft {:fi "Ei löytynyt" :en "Not found"}
                                      :kyc-process.status/wip   {:fi "Ei löytynyt" :en "Not found"}}

         :default                    {:kyc-process.status/draft {:fi "-" :en "-"}
                                      :kyc-process.status/wip   {:fi "-" :en "-"}}}]
    (get-in translations [(or confirmation-status :default) kyc-process-status lang-kw])))

(defn get-confirmation-status-style
  [confirmation-status kyc-process-status]
  (case confirmation-status
    :pending {:color "grey"}
    :confirmed-by-controller {:color (if (= kyc-process-status :kyc-process.status/draft)
                                       "#92C551"
                                       "grey")}
    :confirmed-by-customer {:color "#92C551"}
    :removed-by-controller {:color "#EA5454"}
    :removed-by-customer {:color "#EA5454"}
    :not-found-from-data-source {:color "orange"}
    {:color "grey"}))

(defn board-member-missing-data
  [board-member wip-screen?]
  (and wip-screen?
       (not= (:kyc-board-member/confirmation-status board-member) :removed-by-controller)
       (not= (:kyc-board-member/confirmation-status board-member) :removed-by-customer)
       (or
        (not (:kyc-board-member/first-name board-member))
        (not (:kyc-board-member/last-name board-member))
        (not (:kyc-board-member/role board-member))
        (not (:kyc-board-member/dob board-member))
        (kyc-utils/nationalities-missing-data?
         board-member
         kyc-utils/board-member-nationality-keys)
        (= (:kyc-board-member/role board-member) :unknown-board-role))))

(defn view
  [{:keys [process-id lang-kw question-attribute question-meta
           answers-atom mandatory? read-only? save!]
    :as   args}]
  (let [kyc-process (session/get-entity process-id)
        kyc-process-status (:kyc-process/status kyc-process)
        draft-screen? (= kyc-process-status :kyc-process.status/draft)
        wip-screen? (= kyc-process-status :kyc-process.status/wip)
        board-members (get @answers-atom question-attribute)
        visible-board-members-with-original-idx
        (->>
         board-members                                                                              ; Store original index so board members can be deleted permanently
         (map-indexed (fn [idx board-member]
                        [idx board-member]))
         (filter
          (fn [[_idx board-member]]
            (let [confirmation-status (:kyc-board-member/confirmation-status board-member)]
              (case kyc-process-status
                :kyc-process.status/draft true
                :kyc-process.status/wip
                (some #{confirmation-status} [:confirmed-by-controller
                                              :confirmed-by-customer
                                              :removed-by-customer])
                (some #{confirmation-status} [:confirmed-by-customer
                                              :confirmed-by-controller]))))))
        template (:kyc-process/template kyc-process)
        template-schema (template-registry/templates template)
        ubo-template (get-in template-schema
                             [:kyc-legal-entity/ubo-data :questions :kyc-legal-entity/ubo-edn
                              :data/template])
        board-member-schema (:data/template question-meta)
        set-confirmation-status
        (fn [board-member confirmation-status]
          (let [mapped (map
                        (fn [bm]
                          (if (kyc-utils/are-same-board-members bm board-member)
                            (assoc bm :kyc-board-member/confirmation-status confirmation-status)
                            bm))
                        board-members)]
            (swap! answers-atom assoc question-attribute mapped)
            (save!)))
        show-confirm-all-button
        (cond->> visible-board-members-with-original-idx
          wip-screen?
          (some (fn [[_ bm]]
                  (= (:kyc-board-member/confirmation-status bm) :confirmed-by-controller)))
          (not wip-screen?)
          (some (fn [[_ bm]] (= (:kyc-board-member/confirmation-status bm) :pending))))
        confirm-all-button-disabled
        (->> visible-board-members-with-original-idx
             (filter (fn [[_ bm]]
                       (if wip-screen?
                         (not= (:kyc-board-member/confirmation-status bm) :removed-by-customer)
                         true)))
             (some (fn [[_ bm]]
                     (board-member-missing-data bm wip-screen?))))
        handle-confirm-all
        (fn []
          (let [confirmed-board-members
                (map
                 (fn [bm]
                   (if wip-screen?
                     (if (= (:kyc-board-member/confirmation-status bm) :confirmed-by-controller)
                       (assoc bm :kyc-board-member/confirmation-status :confirmed-by-customer)
                       bm)
                     (if (or
                          (= (:kyc-board-member/confirmation-status bm) :pending)
                          (= (:kyc-board-member/confirmation-status bm)
                             :not-found-from-data-source))
                       (assoc bm :kyc-board-member/confirmation-status :confirmed-by-controller)
                       bm)))
                 board-members)]
            (swap! answers-atom assoc question-attribute confirmed-board-members)
            (save!)))
        make-ubo (fn [board-member]
                   (ubo-editor/open-ubo-editor
                    (merge args
                           {:idx                (count (:kyc-legal-entity/ubo-edn @answers-atom))
                            :question-meta      {:data/template ubo-template}
                            :question-attribute :kyc-legal-entity/ubo-edn
                            :existing-ubo
                            (cond-> {:kyc-ubo/last-name             (:kyc-board-member/last-name board-member)
                                     :kyc-ubo/first-name            (:kyc-board-member/first-name board-member)
                                     :kyc-ubo/middle-names          (:kyc-board-member/middle-names board-member)
                                     :kyc-ubo/country               (:kyc-board-member/country board-member)
                                     :kyc-ubo/dob                   (:kyc-board-member/dob board-member)
                                     :kyc-ubo/primary-ubo-category  :mob-ceo-or-similar
                                     :kyc-ubo/ubo-category-position (translate-board-role
                                                                     (:kyc-board-member/role board-member)
                                                                     lang-kw)}
                              (seq (kyc-utils/nationality-rows board-member
                                                               kyc-utils/board-member-nationality-keys))
                              (assoc :kyc-ubo/nationalities
                                     (kyc-utils/nationality-rows board-member
                                                                 kyc-utils/board-member-nationality-keys)))})))
        show-min-items-label? (and (:min-items question-meta)
                                   (not read-only?))]
    [:div
     [:div.input-editor__label
      [:label
       {:class (uu/cm "input-editor__label-text"
                      (if show-min-items-label?
                        "pb-1" "pb-2")
                      (when mandatory?
                        "after:ml-0.5 after:text-danger-regular after:content-['*']"))}
       (-> question-meta
           :translations
           (get lang-kw))]

      (when show-min-items-label?
        [:p
         {:class "pb-2 text-sm font-normal m-0 text-base-light-text-paler"}
         (loc/tr :add-at-least-n-board-members [(:min-items question-meta)] lang-kw)])]

     (if (and read-only?
              (empty? visible-board-members-with-original-idx))
       [:div.preview
        [:span (loc/tr :no-board-members nil lang-kw)]]
       [:div.max-w-100.overflow-x-scroll.hide-scrollbar
        [:table.zap-table.zap-table--striped.zap-table--has-footer.zap-table--width-auto
         [:thead.zap-table__headline
          [:tr.zap-table__row
           [:th.zap-table__headline ""]
           [:th.zap-table__headline
            (kyc-utils/localize-question board-member-schema :kyc-board-member/first-name lang-kw)]
           [:th.zap-table__headline
            (kyc-utils/localize-question board-member-schema :kyc-board-member/last-name lang-kw)]
           [:th.zap-table__headline
            (kyc-utils/localize-question board-member-schema :kyc-board-member/role lang-kw)]
           [:th.zap-table__headline
            (kyc-utils/localize-question board-member-schema :kyc-board-member/dob lang-kw)]
           [:th.zap-table__headline
            (or (kyc-utils/localize-question board-member-schema :kyc-board-member/nationalities lang-kw)
                (kyc-utils/localize-question board-member-schema :kyc-board-member/country lang-kw))]
           (when-not read-only?
             [:<>
              (when (or draft-screen? wip-screen?)
                [:th.zap-table__headline (kyc-utils/localize-header :confirmation-status lang-kw)])
              (when (or draft-screen? wip-screen?)
                [:th.zap-table__headline (kyc-utils/localize-header :actions lang-kw)])])]]
         [:tbody
          (->>
           visible-board-members-with-original-idx
           (map
            (fn [[idx board-member]]
              (let [confirmation-status (:kyc-board-member/confirmation-status board-member)
                    is-confirmed (or
                                  (= confirmation-status :confirmed-by-customer)
                                  (and (= confirmation-status :confirmed-by-controller)
                                       (not wip-screen?)))
                    requires-action (or
                                     (= confirmation-status :pending)
                                     (= confirmation-status :not-found-from-data-source)
                                     (and (= confirmation-status :confirmed-by-controller)
                                          wip-screen?))
                    is-ubo (is-ubo? board-member answers-atom)
                    missing-data? (board-member-missing-data board-member wip-screen?)]
                [:tr
                 {:key (str "BOARD-MEMBER-" idx "-" (:ord board-member))}
                 [:td.zap-table__cell
                  [:i.fal.fa-external-link.clickable
                   {:on-click (fn [_]
                                (open-board-member-editor
                                 (assoc args
                                        :idx idx
                                        :existing-board-member board-member)))}]]
                 [:td.zap-table__cell
                  (:kyc-board-member/first-name board-member)]
                 [:td.zap-table__cell
                  (:kyc-board-member/last-name board-member)]
                 [:td.zap-table__cell
                  (translate-board-role (:kyc-board-member/role board-member) lang-kw)]
                 [:td.zap-table__cell
                  (when (:kyc-board-member/dob board-member)
                    (fu/format-date-ts (:kyc-board-member/dob board-member)))]
                 [:td.zap-table__cell
                  (kyc-utils/nationality-labels board-member
                                                {:nationality-keys kyc-utils/board-member-nationality-keys
                                                 :lang-kw          lang-kw})]
                 (when-not read-only?
                   [:<>
                    (when (or draft-screen? wip-screen?)
                      [:td.zap-table__cell
                       [:div
                        {:style (get-confirmation-status-style confirmation-status
                                                               kyc-process-status)}
                        (get-confirmation-status-label confirmation-status
                                                       kyc-process-status
                                                       lang-kw)]])
                    (when (or draft-screen? wip-screen?)
                      [:td.zap-table__cell {:style {:min-width "147px"}}
                       [:div.d-flex.align-items-center {:style {:gap 10}}
                        (if requires-action
                          [:<>
                           [button/primary-solid
                            {:on-click (fn [_]
                                         (if missing-data?
                                           (open-board-member-editor
                                            (assoc args
                                                   :idx idx
                                                   :existing-board-member
                                                   (merge board-member
                                                          {:kyc-board-member/confirmation-status
                                                           :confirmed-by-customer})))
                                           (let [new-status (if wip-screen?
                                                              :confirmed-by-customer
                                                              :confirmed-by-controller)]
                                             (set-confirmation-status board-member new-status))))}
                            (loc/tr :confirm nil lang-kw)]
                           [button/warning-solid
                            {:on-click (fn [_]
                                         (let [new-status (if wip-screen?
                                                            :removed-by-customer
                                                            :removed-by-controller)]
                                           (set-confirmation-status board-member new-status)))}
                            (loc/tr :remove nil lang-kw)]]
                          [:<>
                           [button/secondary-hollow
                            {:on-click (fn [_]
                                         (let [new-status (if wip-screen?
                                                            :confirmed-by-controller
                                                            :pending)]
                                           (set-confirmation-status board-member new-status)))
                             :tooltip  (loc/tr :change-status nil lang-kw)}
                            [:i.fal.fa-undo
                             {:aria-hidden true
                              :style       {:margin-right "2px"}}]]

                           (when (and is-confirmed (not is-ubo))
                             [button/primary-solid
                              {:on-click (fn [_] (make-ubo board-member))
                               :tooltip  (loc/tr :set-as-ubo-long nil lang-kw)}
                              (loc/tr :set-as-ubo-short nil lang-kw)])])]])])])))
           (doall))]
         (when-not read-only?
           [:tfoot
            [:tr.zap-table__row
             [:td.zap-table__footer-cell {:col-span "2"}
              [button/primary-solid
               {:on-click (fn [_]
                            (let [idx (count board-members)]
                              (open-board-member-editor (assoc args :idx idx))))}
               [:i.fal.fa-plus.pr-2]
               (loc/tr :add-new nil lang-kw)]]
             [:td.zap-table__footer-cell {:col-span "5"}]
             (when draft-screen?
               [:td.zap-table__footer-cell.d-flex {:col-span "2" :style {:padding-left "12px"}}
                (when show-confirm-all-button
                  [button/primary-solid
                   {:on-click  handle-confirm-all
                    :disabled? confirm-all-button-disabled
                    :tooltip   (when confirm-all-button-disabled
                                 (loc/tr :board-members-missing-data nil lang-kw))}
                   (loc/tr :confirm-all nil lang-kw)])])]])]])]))
